/**
 * Shared, generic tooltip for "every panel header, metric, and control has a
 * short tooltip" (owner feedback 2026-09-27: "no tooltips anywhere"). One
 * floating element, reused across every `attachTooltip` call (cheap: at most
 * one tooltip is ever visible at a time), shown after a short hover delay
 * (so moving the mouse across a busy panel doesn't flash a dozen tooltips)
 * or immediately on keyboard focus (real keyboard accessibility — a
 * `Tab`-reachable control shows its tooltip without waiting for a timer,
 * and `Escape` or blur dismisses it).
 *
 * This is deliberately *not* the neuron-hover tooltip (`src/brain/tooltip.ts`,
 * `NeuronTooltip`) — that one carries live per-neuron taxonomy/activity data
 * from a GPU pick, not a static string, and is wired directly by
 * `BrainPanel`. This module is for the simpler, static case: a panel header
 * explaining what it shows, a button explaining what it does, a metric
 * label explaining what it means.
 */

const DEFAULT_DELAY_MS = 400;

let sharedEl: HTMLDivElement | null = null;
let hideTimer: ReturnType<typeof setTimeout> | null = null;
let showTimer: ReturnType<typeof setTimeout> | null = null;

function ensureSharedElement(): HTMLDivElement {
  if (sharedEl && sharedEl.isConnected) return sharedEl;
  const el = document.createElement("div");
  el.className = "obs-ui-tooltip";
  el.setAttribute("role", "tooltip");
  document.body.appendChild(el);
  sharedEl = el;
  return el;
}

function clearTimers(): void {
  if (showTimer !== null) {
    clearTimeout(showTimer);
    showTimer = null;
  }
  if (hideTimer !== null) {
    clearTimeout(hideTimer);
    hideTimer = null;
  }
}

function positionNear(el: HTMLDivElement, anchor: HTMLElement): void {
  const rect = anchor.getBoundingClientRect();
  const margin = 6;
  // Default: below the anchor, left-aligned to it; flipped above if there
  // isn't room below (a header near the bottom of the viewport, etc.).
  el.style.left = `${Math.max(4, Math.min(rect.left, window.innerWidth - el.offsetWidth - 4))}px`;
  const spaceBelow = window.innerHeight - rect.bottom;
  if (spaceBelow < el.offsetHeight + margin && rect.top > el.offsetHeight + margin) {
    el.style.top = `${rect.top - el.offsetHeight - margin}px`;
  } else {
    el.style.top = `${rect.bottom + margin}px`;
  }
}

function showNow(anchor: HTMLElement, text: string): void {
  const el = ensureSharedElement();
  el.textContent = text;
  el.classList.add("obs-visible");
  positionNear(el, anchor);
}

function hideNow(): void {
  sharedEl?.classList.remove("obs-visible");
}

export interface TooltipOptions {
  /** Hover delay before the tooltip appears (ms). Keyboard focus always
   * shows immediately, regardless of this value — a delay there would make
   * the tooltip feel broken to a keyboard user. */
  readonly delayMs?: number;
}

/** Wires hover (delayed) + keyboard-focus (immediate) + `Escape`/blur/leave
 * dismissal for one element. `text` may be a string or a thunk (read fresh
 * each time the tooltip is about to show, for content that changes, e.g. a
 * live "last updated" clock). Returns a cleanup function. Idempotent to
 * call twice on the same element (the second call's listeners simply stack;
 * callers should not double-attach in practice, but nothing breaks). */
export function attachTooltip(
  el: HTMLElement,
  text: string | (() => string),
  options: TooltipOptions = {},
): () => void {
  const delayMs = options.delayMs ?? DEFAULT_DELAY_MS;
  const resolve = (): string => (typeof text === "function" ? text() : text);

  // Keyboard accessibility: a control that isn't already focusable (e.g. a
  // plain `<span>` metric label) needs `tabindex` to ever receive focus.
  if (el.tabIndex < 0 && !["BUTTON", "A", "INPUT", "SELECT", "TEXTAREA"].includes(el.tagName)) {
    el.tabIndex = 0;
  }

  const onEnter = (): void => {
    clearTimers();
    showTimer = setTimeout(() => showNow(el, resolve()), delayMs);
  };
  const onLeave = (): void => {
    clearTimers();
    hideNow();
  };
  const onFocus = (): void => {
    clearTimers();
    showNow(el, resolve());
  };
  const onBlur = (): void => {
    clearTimers();
    hideNow();
  };
  const onKeyDown = (event: KeyboardEvent): void => {
    if (event.key === "Escape") {
      clearTimers();
      hideNow();
    }
  };

  el.addEventListener("mouseenter", onEnter);
  el.addEventListener("mouseleave", onLeave);
  el.addEventListener("focus", onFocus);
  el.addEventListener("blur", onBlur);
  el.addEventListener("keydown", onKeyDown);
  // Static text also becomes the accessible name (screen readers, not just
  // the visual hover/focus tooltip); a thunk is left alone here since
  // resolving it now (before any real hover/focus) could have side effects
  // and would go stale immediately for live content anyway.
  if (typeof text === "string" && !el.hasAttribute("aria-label") && !el.title) {
    el.setAttribute("aria-label", text);
  }

  return () => {
    el.removeEventListener("mouseenter", onEnter);
    el.removeEventListener("mouseleave", onLeave);
    el.removeEventListener("focus", onFocus);
    el.removeEventListener("blur", onBlur);
    el.removeEventListener("keydown", onKeyDown);
  };
}

/** Test/dev hook: disposes the shared tooltip element entirely. Not used in
 * production (the element is cheap and lives for the page's lifetime). */
export function resetSharedTooltip(): void {
  clearTimers();
  sharedEl?.remove();
  sharedEl = null;
}
